import type { BulkResultItem } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';

/** The call that failed: the same code means different things on different calls. */
export type ScheduleAction =
  | 'create'
  | 'update'
  | 'delete'
  | 'activate'
  | 'pause'
  | 'resume'
  | 'end'
  | 'runNow'
  | 'preview'
  | 'addMembers'
  | 'updateMember'
  | 'removeMember';

/** Another schedule owns one of these products in a target store (422 SCHEDULE_CONFLICT). */
export interface ScheduleConflict {
  scheduleId: string | null;
  productIds: string[];
  storeIds: string[];
}

/** An i18n key in the `schedules` namespace and what it interpolates; store ids still to name. */
export interface ScheduleErrorCopy {
  key: string;
  values: Record<string, string | number>;
  /** Interpolated as `{{store}}` once the caller has looked the name up. */
  storeId?: string;
  conflict?: ScheduleConflict;
}

const ACTIVATE_REASONS: ReadonlySet<string> = new Set(['no_stores', 'no_candidates', 'ended']);
/** Statuses `SCHEDULE_NOT_ACTIVE` can name; each has its own sentence, never the raw value. */
const INACTIVE_STATUSES: ReadonlySet<string> = new Set(['draft', 'paused', 'ended']);

const strings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];

const text = (value: unknown): string | undefined =>
  typeof value === 'string' && value !== '' ? value : undefined;

export function scheduleConflictOf(error: unknown): ScheduleConflict | null {
  if (!isApiError(error) || error.code !== 'SCHEDULE_CONFLICT') return null;
  return {
    scheduleId: text(error.meta.scheduleId) ?? null,
    productIds: strings(error.meta.productIds),
    storeIds: strings(error.meta.storeIds),
  };
}

/**
 * Copy for every refusal the schedule routes answer with, or null to fall back to the generic
 * `errors:code.<CODE>` message (validation details, not found, server errors).
 */
export function scheduleErrorCopy(
  error: unknown,
  action: ScheduleAction,
): ScheduleErrorCopy | null {
  if (!isApiError(error)) return null;
  const { meta } = error;
  const storeId = text(meta.storeId);
  const status = text(meta.status);
  switch (error.code) {
    case 'SCHEDULE_CONFLICT': {
      const conflict = scheduleConflictOf(error) ?? {
        scheduleId: null,
        productIds: [],
        storeIds: [],
      };
      return {
        key: conflict.scheduleId ? 'errors.conflict' : 'errors.conflictRace',
        values: { count: Math.max(conflict.productIds.length, 1) },
        conflict,
      };
    }
    case 'SCHEDULE_NOT_ACTIVE':
      return {
        key: `errors.notActive.${status && INACTIVE_STATUSES.has(status) ? status : 'other'}`,
        values: {},
      };
    case 'LIMIT_EXCEEDED': {
      const limit = typeof meta.limit === 'number' ? meta.limit : 0;
      return action === 'create' && !error.fieldErrors['body.productIds']
        ? { key: 'errors.scheduleLimit', values: { limit } }
        : { key: 'errors.memberLimit', values: { limit } };
    }
    case 'STORE_READ_ONLY':
      return { key: 'errors.storeReadOnly', values: {}, ...(storeId ? { storeId } : {}) };
    case 'ALREADY_EXISTS': {
      const handle = text(meta.handle);
      const count = Math.max(strings(meta.productIds).length, 1);
      return {
        key: handle ? 'errors.handleTakenNamed' : 'errors.handleTaken',
        values: { count, handle: handle ?? '' },
        ...(storeId ? { storeId } : {}),
      };
    }
    case 'INVALID_REFERENCE': {
      const productIds = strings(meta.productIds);
      if (productIds.length > 0) {
        return { key: 'errors.unknownProducts', values: { count: productIds.length } };
      }
      return null;
    }
    case 'INVALID_INPUT': {
      const reason = text(meta.reason);
      if (reason && ACTIVATE_REASONS.has(reason)) {
        return { key: `errors.activate.${reason}`, values: {} };
      }
      return null;
    }
    case 'CONFLICT': {
      if (action === 'runNow' && text(meta.runId))
        return { key: 'errors.runInProgress', values: {} };
      if (status === 'ended') return { key: 'errors.ended', values: {} };
      if (action === 'delete') return { key: 'errors.cannotDelete', values: {} };
      if (action === 'activate') return { key: 'errors.notDraft', values: {} };
      if (action === 'resume') return { key: 'errors.notPaused', values: {} };
      return null;
    }
    default:
      return null;
  }
}

/**
 * Copy for one refused id of a `BulkResponse` (a pool add, or `bulkMembers`), from its `code` and
 * `meta`: the store and handle of `ALREADY_EXISTS`, the owner of `SCHEDULE_CONFLICT`. Null when the
 * generic `errors:code.<CODE>` line already says it all.
 */
export function scheduleItemCopy(
  item: Pick<BulkResultItem, 'code' | 'meta'>,
): ScheduleErrorCopy | null {
  const meta = item.meta ?? {};
  const storeId = text(meta.storeId);
  const store = storeId ? { storeId } : {};
  // Read as a plain string: only a few of the many codes have copy of their own.
  const code: string = item.code ?? '';
  switch (code) {
    case 'SCHEDULE_CONFLICT': {
      const scheduleId = text(meta.scheduleId) ?? null;
      return {
        key: scheduleId ? 'errors.conflict' : 'errors.conflictRace',
        values: { count: 1 },
        conflict: { scheduleId, productIds: [], storeIds: strings(meta.storeIds) },
      };
    }
    case 'ALREADY_EXISTS': {
      const handle = text(meta.handle);
      return {
        key: handle ? 'errors.handleTakenNamed' : 'errors.handleTaken',
        values: { count: 1, handle: handle ?? '' },
        ...store,
      };
    }
    case 'STORE_READ_ONLY':
      return { key: 'errors.storeReadOnly', values: {}, ...store };
    case 'LIMIT_EXCEEDED':
      return {
        key: 'errors.memberLimit',
        values: { limit: typeof meta.limit === 'number' ? meta.limit : 5000 },
      };
    default:
      return null;
  }
}
